/**
 * PrintBridge.js — Environment-aware print router.
 *
 * SINGLE entry point for ALL printing in the AgriSmart Terminal.
 * Routes print calls to the correct execution path:
 *
 *   Capacitor APK (H10P device):
 *     PrintEngine.generateHtml() → local QR data URL → H10PPrinterPlugin.printHtml() → native SDK → 58mm paper
 *
 *   Web browser (desktop admin / dev):
 *     PrintEngine.print() → window.open() + window.print() → browser print dialog
 *
 * IMPORTANT: Do NOT call PrintEngine.print() directly from terminal components.
 *            Always use PrintBridge.print() so the H10P printer works.
 *
 * Affected call sites (these import PrintBridge instead of PrintEngine):
 *   - TerminalSales.jsx       (sale receipt after checkout)
 *   - TerminalShell.jsx       (QuickScan sheet reprint buttons)
 *   - DocViewerPage.jsx       (Tier 2 reprint buttons)
 *
 * Non-terminal pages (SalesPage, BranchTransferPage, etc.) continue using
 * PrintEngine.print() directly — they are desktop admin pages, not H10P pages.
 */
import { Capacitor } from '@capacitor/core';
import { toast } from 'sonner';
import PrintEngine from './PrintEngine';
import { applyPrintExtras } from './docExtras';
import PrintOps from './PrintOps';
import { H10PPrinter } from './H10PPrinterPlugin';
import QRCode from 'qrcode'; // static import — ensures it's always bundled (dynamic import can fail in Capacitor WebView)

// ── Print-path observability ────────────────────────────────────────────────
// The H10P has no visible console, so "which path printed?" must be knowable
// without adb: (a) ring-buffer log in localStorage (PrintBridge.getPrintLog()),
// (b) toast when a text-mode print FALLS BACK to bitmap, (c) fire-and-forget
// report to /error-reports so fallbacks appear in the admin Error Log remotely.
const PRINT_LOG_KEY = 'agribooks_print_log';
const PRINT_LOG_MAX = 50;

function logPrintEvent(entry) {
  const record = { ts: new Date().toISOString(), ...entry };
  try {
    const log = JSON.parse(localStorage.getItem(PRINT_LOG_KEY) || '[]');
    log.unshift(record);
    localStorage.setItem(PRINT_LOG_KEY, JSON.stringify(log.slice(0, PRINT_LOG_MAX)));
  } catch { /* localStorage unavailable — console below still fires */ }
  // eslint-disable-next-line no-console
  console.log('[PrintBridge]', record);
}

// ── TEXT MODE MASTER SWITCH ─────────────────────────────────────────────────
// First on-paper test (2026-07-13): built-in font is thicker/sharper, BUT the
// ops layout doesn't match the web receipt (printTableText weight/align
// semantics differ from assumptions) and printQRCode at module 4 printed
// blurry/small. DISABLED by default until the ops layout is tuned on-device;
// the bitmap path now carries the font+viewport fixes, so it prints the
// web-identical layout with the improved text.
// Per-device override for tuning WITHOUT a redeploy: in the terminal WebView
// run  localStorage.setItem('agribooks_textmode', 'on')  (or 'off' to force).
const TEXT_MODE_DEFAULT = false;
function textModeEnabled() {
  try {
    const o = localStorage.getItem('agribooks_textmode');
    if (o === 'on') return true;
    if (o === 'off') return false;
  } catch { /* ignore */ }
  return TEXT_MODE_DEFAULT;
}

let _fallbackReported = false;   // one remote report per session is enough
let _textModeToastShown = false; // confirm text mode once per session, not per receipt

async function reportFallbackToAdmin(entry) {
  if (_fallbackReported) return;
  _fallbackReported = true;
  try {
    const { api } = await import('../contexts/AuthContext');
    await api.post('/error-reports', {
      page: window.location.pathname,
      action: 'Thermal print fell back to bitmap mode',
      error_message: `printOps failed for ${entry.type}: ${entry.fallbackReason}`,
      component: 'PrintBridge',
      browser_info: navigator.userAgent,
      extra: { timestamp: entry.ts, print_log_tail: entry },
    });
  } catch { /* offline / unauthenticated — local log still has it */ }
}

/**
 * Replace the api.qrserver.com QR image URL with a locally-generated
 * base64 PNG data URL using the 'qrcode' npm package.
 *
 * Why: The H10P renders HTML in a headless Android WebView. External network
 * requests from that WebView are unreliable and slow. Generating locally
 * guarantees the QR code is always embedded and renders instantly.
 */
async function replaceQrWithLocalDataUrl(html) {
  // Match EVERY qrserver URL, not just the first — multi-QR documents (e.g. a
  // statement with per-invoice QRs) previously kept live remote URLs for QRs
  // 2..n, which race the native bitmap capture.
  const qrUrls = [...new Set(
    [...html.matchAll(/src="(https:\/\/api\.qrserver\.com[^"]+)"/g)].map(m => m[1])
  )];
  if (!qrUrls.length) return html;

  for (const qrUrl of qrUrls) {
    let qrData = null;
    try {
      qrData = new URL(qrUrl).searchParams.get('data');
    } catch (_) { /* malformed URL, skip */ }
    if (!qrData) continue;

    try {
      const dataUrl = await QRCode.toDataURL(qrData, {
        width: 152,
        margin: 1,
        errorCorrectionLevel: 'M',
        color: { dark: '#000000', light: '#ffffff' },
      });
      html = html.split(qrUrl).join(dataUrl);
    } catch (e) {
      console.warn('[PrintBridge] Local QR generation failed, removing QR img:', e);
      html = html.replace(/<img[^>]*api\.qrserver\.com[^>]*>/gi, '');
      break; // remaining remote imgs were removed by the sweep above
    }
  }
  return html;
}

const PrintBridge = {
  /**
   * Cached capability descriptor reported by the native H10P plugin.
   * - awaitsCompletion: when true, `printHtml()` resolves only after the
   *   printer head has stopped feeding paper. In that mode the multi-copy
   *   loop only needs a small "cut buffer" between copies.
   * - When false (or capability unknown), we fall back to the
   *   `estimatePrintMs()` heuristic so the next copy doesn't queue up
   *   while the printer is still mid-print.
   *
   * Filled lazily on first call to `getCapabilities()`. Once Cursor ships
   * the APK fix (CURSOR_PROMPT_PRINT_COMPLETION.md), the plugin will expose
   * a `getCapabilities()` method that returns `{ awaitsCompletion: true,
   * version: "..." }` and the web side will automatically pick the simple
   * 2-second-gap path without further code changes here.
   */
  _capabilities: null,

  /**
   * Returns the native plugin's capability set, cached after first call.
   * Always resolves — never throws — so the multi-copy loop can rely on it.
   * Shape: `{ awaitsCompletion: boolean, version: string }`.
   *
   * APK contract (to be implemented by Cursor):
   *   H10PPrinter.getCapabilities() returns
   *     { awaitsCompletion: true,  version: "1.0" }   // after fix
   *   Legacy / web environment / pre-fix APK returns
   *     { awaitsCompletion: false, version: "0.x" }   // default fallback
   */
  async getCapabilities() {
    if (this._capabilities) return this._capabilities;
    const fallback = { awaitsCompletion: false, textMode: false, version: '0.legacy' };
    if (!Capacitor.isNativePlatform()) {
      this._capabilities = fallback;
      return fallback;
    }
    try {
      // Plugin may not implement this method yet — that's fine, treat as legacy.
      const caps = await H10PPrinter.getCapabilities();
      this._capabilities = {
        awaitsCompletion: !!caps?.awaitsCompletion,
        // textMode: the APK implements printOps (SDK text commands — built-in
        // printer font). Old APKs never report it → HTML→bitmap path as before.
        textMode: !!caps?.textMode,
        version: caps?.version || '1.0',
      };
    } catch (_) {
      this._capabilities = fallback;
    }
    return this._capabilities;
  },

  /**
   * Estimate the wall-clock time (ms) a thermal receipt will spend feeding
   * paper through the H10P head. `H10PPrinter.printHtml()` is fire-and-forget
   * on the native side — the await resolves the instant the job is dispatched
   * to the printer SDK, NOT when paper stops moving. For multi-copy reprints
   * this means the inter-copy "cut buffer" timer starts too early and the
   * second copy spools while the first is still mid-print.
   *
   * Multi-copy callers should use this to size the gap between copies:
   *
   *     await PrintBridge.print(opts);
   *     await sleep(PrintBridge.estimatePrintMs(opts) + CUT_BUFFER_MS);
   *
   * Heuristic (calibrated against the H10P @ ~80 mm/s, ~6 lines/inch):
   *   header/qr/footer constant ≈ 1.8 s
   *   each line item ≈ 0.18 s  (line + qty/price formatting can wrap)
   *   each payment / discount footer row ≈ 0.15 s
   * Clamped to [2.5 s, 12 s] so callers never wait too little or absurdly long.
   *
   * For non-thermal formats (full-page A4 via window.print()) we return a
   * small constant — the user-facing flow is "print dialog already open" so
   * no inter-copy buffer is meaningful.
   */
  estimatePrintMs({ data, format = 'thermal' } = {}) {
    if (format !== 'thermal') return 1500;
    const items = Array.isArray(data?.items) ? data.items.length : 0;
    const payments = Array.isArray(data?.payments) ? data.payments.length : 0;
    const lines = Array.isArray(data?.lines) ? data.lines.length : 0;  // PP / statements
    const rows = items + payments + lines;
    const ms = 1800 + rows * 180;
    return Math.max(2500, Math.min(12000, ms));
  },

  /**
   * Decide how long to wait BETWEEN copies in a multi-copy reprint loop.
   *
   *   1. If the native plugin reports `awaitsCompletion: true` (Cursor's APK
   *      fix is in), `await PrintBridge.print()` already blocks until paper
   *      stops feeding — so we only need the 2 s tear / cut buffer.
   *   2. Otherwise (legacy APK or web), `print()` returns on dispatch, so we
   *      add an estimated print duration on top of the 2 s buffer.
   *
   * This is the single source of truth for inter-copy timing. Both
   * ReprintModal and TerminalSales use it so behaviour stays consistent and
   * — once the APK is fixed — they automatically drop to the simple 2 s gap.
   *
   * @returns {Promise<number>} milliseconds to wait between two `print()` calls.
   */
  async interCopyGapMs({ data, format = 'thermal' } = {}) {
    const CUT_BUFFER_MS = 2000;
    const caps = await this.getCapabilities();
    if (caps.awaitsCompletion) return CUT_BUFFER_MS;
    return this.estimatePrintMs({ data, format }) + CUT_BUFFER_MS;
  },

  /**
   * Main print function — matches PrintEngine.print() signature exactly.
   * Drop-in replacement: swap `import PrintEngine` → `import PrintBridge`.
   * @param {number} [opts.feedLinesAfter] - Blank lines to feed after receipt (default controlled by Java). Use 6–10 if tear cut is tight.
   */
  async print({ type, data: rawData, format = 'thermal', businessInfo = {}, docCode = '',
                copyNumber = 1, feedLinesAfter, extras = null } = {}) {
    // Same contract as PrintEngine.print: the operator's answer to the print
    // prompt is applied ONCE here, so the native text-mode path (PrintOps) and
    // the HTML->bitmap path below cannot disagree about what was withheld.
    // No answer (a queued job, a QuickScan reprint that did not ask) prints
    // everything, which is the old behaviour.
    const data = applyPrintExtras(rawData, extras);
    if (Capacitor.isNativePlatform()) {
      // Server-side print history — the browser branch logs inside
      // PrintEngine.print; the native path must log here.
      PrintEngine.registerPrintEvent?.(type, data, format);
      const clampFeed = (payload) => {
        if (feedLinesAfter != null && Number.isFinite(Number(feedLinesAfter))) {
          payload.feedLinesAfter = Math.max(0, Math.min(24, Math.round(Number(feedLinesAfter))));
        }
        return payload;
      };

      // TEXT-MODE path (preferred): APK reports `textMode` → send structured ops
      // and the printer renders with its BUILT-IN FONT (crisp at any length, no
      // giant-bitmap cutoff). Only for thermal docs that have an ops builder.
      // (The reprint banner only exists for official_receipt, which has no ops
      // builder — so copies 2+ print identically via ops.) Any failure falls
      // through to the proven HTML→bitmap path below.
      let fallbackReason = null;
      if (format === 'thermal' && textModeEnabled() && PrintOps.canRenderOps(type)) {
        const caps = await this.getCapabilities();
        if (caps.textMode) {
          try {
            // HYBRID TAIL: the printer's native printQRCode renders blurry and
            // small text sizes print with inconsistent thickness (2026-07-14
            // on-paper tests) — while the bitmap path is crisp for both. So:
            // job 1 = body as text ops (fast, built-in font, no QR/footer),
            // job 2 = bitmap tail with QR + disclaimer, correct order:
            // body → QR → disclaimer.
            const { version, ops } = PrintOps.generateOps({ type, data, businessInfo, docCode, omitQr: true, omitFooter: true });
            await H10PPrinter.printOps({ version, ops, feedLinesAfter: 0 });
            {
              const foot = PrintOps.getFooterLines(type, { isDraft: data?.status === 'for_preparation' });
              const url = docCode ? `${window.location.origin}/doc/${docCode}` : '';
              let tail = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=384, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"><title>QR</title></head><body style="margin:0;padding:1px 3px;text-align:center;font-family:'Consolas','Roboto Mono','Droid Sans Mono',monospace;color:#000">`;
              if (url) {
                tail += `<img src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(url)}&margin=2" width="120" height="120" style="display:block;margin:0 auto"/>`;
                tail += `<div style="font-size:11px;font-weight:bold;letter-spacing:1px">${docCode}</div>`;
                tail += `<div style="font-size:9px">Scan to view document</div>`;
              }
              tail += `<div style="border-top:1px solid #000;margin-top:4px;padding-top:3px;font-size:9px;line-height:1.2">`;
              if (foot.isDraft) tail += `<div style="font-weight:bold">DRAFT - not yet a valid transaction.</div>`;
              if (foot.title) tail += `<div style="font-weight:bold">${foot.title}</div>`;
              for (const line of foot.body) tail += `<div>${line}</div>`;
              tail += `</div></body></html>`;
              tail = await replaceQrWithLocalDataUrl(tail);
              await H10PPrinter.printHtml(clampFeed({ html: tail, format: 'thermal' }));
            }
            logPrintEvent({ via: 'textmode', type, format });
            if (!_textModeToastShown) {
              _textModeToastShown = true;
              toast.success('Printed via text mode (built-in printer font)');
            }
            return { success: true, via: 'textmode' };
          } catch (err) {
            fallbackReason = err?.message || String(err);
            const entry = { via: 'bitmap-fallback', type, format, fallbackReason };
            logPrintEvent(entry);
            toast.warning('Printed in compatibility mode (bitmap)', {
              description: `Text mode failed: ${fallbackReason}`.slice(0, 140),
              duration: 8000,
            });
            reportFallbackToAdmin({ ...entry, ts: new Date().toISOString() }); // fire-and-forget
          }
        } else {
          // Old APK (no textMode capability) — expected on un-updated devices;
          // logged so getPrintLog() explains WHY bitmap mode is in use.
          logPrintEvent({ via: 'bitmap', type, format, note: 'APK reports no textMode capability' });
        }
      }

      // Native H10P path: generate HTML, replace QR with local data URL, then send to printer SDK
      try {
        let html = PrintEngine.generateHtml({ type, data, format, businessInfo, docCode, copyNumber });
        html = await replaceQrWithLocalDataUrl(html);
        const payload = clampFeed({ html, format });
        await H10PPrinter.printHtml(payload);
        if (fallbackReason == null) logPrintEvent({ via: 'bitmap', type, format });
        return { success: true, via: fallbackReason ? 'bitmap-fallback' : 'bitmap', fallbackReason: fallbackReason || undefined };
      } catch (err) {
        console.error('[PrintBridge] Native print failed:', err);
        logPrintEvent({ via: 'bitmap', type, format, error: err?.message || String(err) });
        throw err;
      }
    } else {
      // Browser path: use existing PrintEngine (opens popup + window.print())
      PrintEngine.print({ type, data, format, businessInfo, docCode, copyNumber });
      return { success: true, via: 'browser' };
    }
  },

  /**
   * Print-path debug log — last 50 print attempts with which path was used
   * (`textmode` | `bitmap` | `bitmap-fallback` | `browser`) and any failure
   * reason. Readable on-device without adb: from any page's devtools-less
   * context via `PrintBridge.getPrintLog()` (e.g. surfaced in a settings/debug
   * view), and each entry is also console-logged for tethered debugging.
   */
  getPrintLog() {
    try { return JSON.parse(localStorage.getItem(PRINT_LOG_KEY) || '[]'); }
    catch { return []; }
  },

  clearPrintLog() {
    try { localStorage.removeItem(PRINT_LOG_KEY); } catch { /* ignore */ }
  },

  /**
   * Check if the native printer is connected (H10P only).
   * Returns { connected: true/false }.
   * Always returns { connected: false } in a browser.
   */
  async checkPrinterStatus() {
    if (!Capacitor.isNativePlatform()) {
      return { connected: false };
    }
    try {
      return await H10PPrinter.checkStatus();
    } catch {
      return { connected: false };
    }
  },

  /** True when running inside the Capacitor APK */
  isNative() {
    return Capacitor.isNativePlatform();
  },

  /** Pass-through so callers don't need to import PrintEngine for doc type detection */
  getDocType(invoice) {
    return PrintEngine.getDocType(invoice);
  },

  /**
   * Print the text-mode CALIBRATION STRIP (H10P only) — labeled experiments
   * for table weights/aligns, size scale, style flags, and QR module sizes.
   * Bypasses the textModeEnabled gate: it's an explicit diagnostic action.
   * Read the paper, report which lines look right → encode real rules in
   * PrintOps. Triggered from Terminal Settings → "Print calibration strip".
   */
  async printCalibration() {
    if (!Capacitor.isNativePlatform()) {
      throw new Error('Calibration prints on the H10P terminal only.');
    }
    const { version, ops } = PrintOps.generateCalibrationOps();
    await H10PPrinter.printOps({ version, ops, feedLinesAfter: 6 });
    logPrintEvent({ via: 'textmode-calibration' });
  },

  /** Text-mode tuning switch state (per-device localStorage override). */
  getTextModeSetting() {
    try { return localStorage.getItem('agribooks_textmode') || 'default-off'; }
    catch { return 'default-off'; }
  },
  setTextModeSetting(v) {
    try {
      if (v === 'on' || v === 'off') localStorage.setItem('agribooks_textmode', v);
      else localStorage.removeItem('agribooks_textmode');
    } catch { /* ignore */ }
  },

  /** Advance paper only (H10P). No-op in browser. */
  async feedPaper(lines = 8) {
    if (!Capacitor.isNativePlatform()) return;
    const n = Math.max(1, Math.min(40, Math.round(Number(lines)) || 8));
    await H10PPrinter.feedPaper({ lines: n });
  },

  /**
   * Print a customer account statement (Open Invoices / History / Payments).
   * Generates 58mm HTML via statementThermal, then routes to native H10P or
   * browser popup — same routing as PrintBridge.print().
   *
   * @param {object} data        - account-summary API response
   * @param {object} businessInfo
   * @param {'open_invoices'|'history'|'payments'} printType
   */
  async printStatement(data, businessInfo, printType) {
    const { statementThermal } = await import('./PrintEngine');
    const html = statementThermal(data, businessInfo || {}, printType);

    if (Capacitor.isNativePlatform()) {
      try {
        const patchedHtml = await replaceQrWithLocalDataUrl(html);
        await H10PPrinter.printHtml({ html: patchedHtml, format: 'thermal' });
      } catch (err) {
        console.error('[PrintBridge] printStatement native failed:', err);
        throw err;
      }
    } else {
      // Browser: rasterize to a 384px bitmap (parity with the H10P native path)
      // so desktop thermal statements print as thick/crisp as the terminal instead
      // of thin anti-aliased vector text. Falls back to the plain popup internally.
      await PrintEngine.printThermalDocument(html);
    }
  },
};

export default PrintBridge;
